import { getApiBaseUrl } from '../config/runtime';
import type {
  ApiErrorBody,
  RemarketingErasureResponse,
  RemarketingLeadListResponse,
  RemarketingJourneyListResponse,
  RemarketingJourneyStep,
  RemarketingJourneyStatus,
  CheckoutDetails,
  CheckoutListResponse,
  EventListResponse,
  FunnelResponse,
  IdentityEraseResponse,
  OverviewMetrics
} from '../types/api';

export const AUTH_UNAUTHORIZED_EVENT = 'auth:unauthorized';

export class ApiError extends Error {
  status: number;
  body: ApiErrorBody | null;
  constructor(status: number, message: string, body: ApiErrorBody | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

type Query = Record<string, string | number | undefined | null>;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'DELETE';
  query?: Query;
  /** Corpo JSON — é por aqui que PII trafega, nunca pela query. */
  body?: unknown;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: Query): string {
  const url = new URL(`${getApiBaseUrl()}${path}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

/**
 * Mensagem por status. O proxy do Pages e o Worker falham de formas
 * DIFERENTES, e colapsar tudo em "falha no servidor" esconde justamente o
 * estado mais provável durante o rollout (proxy/Access sem configuração).
 */
/** Falha sem status HTTP (rede ou resposta não-JSON). */
export function describeNetworkError(): string {
  return 'Não foi possível falar com o painel. Verifique sua conexão e tente novamente.';
}

export function describeApiError(status: number, body: ApiErrorBody | null): string {
  const code = typeof body?.error === 'string' ? body.error : '';

  if (status === 401) {
    return 'Sua sessão expirou. Entre novamente.';
  }
  if (status === 403) {
    // `insufficient_scope` = a identidade vale, o poder é que falta — relogar
    // não resolve, e dizer "sessão expirada" mandaria o operador ao lugar errado.
    if (code === 'insufficient_scope') {
      return 'Sua conta não tem permissão para esta operação.';
    }
    if (code === 'cross_site_request_blocked') {
      return 'Requisição bloqueada por segurança. Recarregue o painel e tente de novo.';
    }
    return 'Acesso negado: sua identidade não tem permissão para este painel.';
  }
  if (status === 404) return 'Recurso não encontrado.';
  if (status === 429) return 'Muitas requisições, tente novamente em instantes.';
  if (status === 502) {
    return 'Não foi possível falar com o serviço de rastreamento.';
  }
  if (status === 503) {
    if (code === 'proxy_not_configured' || code === 'auth_not_configured') {
      return 'Painel ainda não configurado neste ambiente.';
    }
    if (code === 'auth_not_configured') {
      return 'As rotas administrativas estão desabilitadas neste ambiente.';
    }
    if (code === 'rate_limit_unavailable') {
      return 'Limite de requisições indisponível — tente novamente em instantes.';
    }
    return 'Serviço temporariamente indisponível.';
  }
  if (status >= 500) return 'Falha temporária no servidor.';
  return code || `HTTP ${status}`;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', query, body, signal } = options;
  // Nenhuma credencial sai daqui: a sessão é um cookie HttpOnly que o browser
  // anexa sozinho e o JavaScript não consegue ler.
  const headers: Record<string, string> = { Accept: 'application/json' };

  let res: Response;
  try {
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    res = await fetch(buildUrl(path, query), {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      signal
    });
  } catch (err) {
    // AbortError é fluxo normal (StrictMode/troca de filtro) — os hooks
    // dependem de recebê-lo cru. Checagem por NOME, não por tipo: dependendo
    // do runtime o abort chega como DOMException ou Error, e engolir isso num
    // ApiError pintaria a tela de erro a cada remontagem.
    if ((err as { name?: string } | null)?.name === 'AbortError') throw err;
    // No modo proxy a causa mais comum é a sessão do Cloudflare Access ter
    // expirado: o Access responde com redirect para o login em OUTRA origem,
    // que o fetch não consegue seguir e vira erro de rede — nunca um status.
    throw new ApiError(0, describeNetworkError(), null);
  }

  let parsed: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = { raw: text };
    }
  }

  // Resposta "ok" que não é JSON = quase sempre a tela de login do Access
  // servida no lugar da API. Sem isto, o HTML seguiria como se fosse dado e a
  // página quebraria com um erro sem relação com a causa.
  if (res.ok && !(res.headers.get('content-type') ?? '').includes('json')) {
    throw new ApiError(res.status, describeNetworkError(), null);
  }

  if (!res.ok) {
    // 401 = sessão morta. O proxy já expirou o cookie; avisamos o app para
    // refletir isso e mandar o operador ao login.
    if (res.status === 401 && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));
    }
    const body = (parsed as ApiErrorBody | null) ?? null;
    throw new ApiError(res.status, describeApiError(res.status, body), body);
  }

  return parsed as T;
}

export interface ListCheckoutsParams {
  from: string;
  to: string;
  status?: string;
  source?: string;
  limit?: number;
  cursor?: string | null;
}

export interface ListEventsParams {
  from: string;
  to: string;
  eventName?: string;
  limit?: number;
  cursor?: string | null;
}

export interface MetricsParams {
  from: string;
  to: string;
  source?: string;
}

export interface ListLeadsParams {
  eventId?: string;
  organizationId?: string;
  source?: string;
  createdFrom?: string;
  createdTo?: string;
  limit?: number;
  cursor?: string | null;
}

export interface ListJourneysParams {
  eventId?: string;
  organizationId?: string;
  step?: RemarketingJourneyStep;
  status?: RemarketingJourneyStatus;
  activityFrom?: string;
  activityTo?: string;
  hasContact?: 'true' | 'false';
  limit?: number;
  cursor?: string | null;
}

/**
 * Remarketing. Passa pelo MESMO BFF de mesma origem: o browser não conhece o
 * endereço do serviço nem carrega credencial.
 *
 * Só IDs e datas vão na query string. O e-mail do titular — PII — viaja no
 * CORPO do erasure, porque URL aparece em access log, referer e histórico.
 */
export const remarketingApi = {
  listJourneys(
    params: ListJourneysParams,
    signal?: AbortSignal
  ): Promise<RemarketingJourneyListResponse> {
    return request('/remarketing/journeys', {
      query: {
        eventId: params.eventId,
        organizationId: params.organizationId,
        step: params.step,
        status: params.status,
        activityFrom: params.activityFrom,
        activityTo: params.activityTo,
        hasContact: params.hasContact,
        limit: params.limit,
        cursor: params.cursor
      },
      signal
    });
  },

  listLeads(
    params: ListLeadsParams,
    signal?: AbortSignal
  ): Promise<RemarketingLeadListResponse> {
    return request<RemarketingLeadListResponse>('/remarketing/leads', {
      query: {
        eventId: params.eventId,
        organizationId: params.organizationId,
        source: params.source,
        createdFrom: params.createdFrom,
        createdTo: params.createdTo,
        limit: params.limit,
        cursor: params.cursor ?? undefined
      },
      signal
    });
  },

  /** URL do download; o navegador baixa direto do BFF (streaming preservado). */
  exportUrl(params: Omit<ListLeadsParams, 'limit' | 'cursor'>): string {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value) query.set(key, String(value));
    }
    const suffix = query.toString();
    return `/api/remarketing/leads/export${suffix ? `?${suffix}` : ''}`;
  },

  eraseLead(email: string, signal?: AbortSignal): Promise<RemarketingErasureResponse> {
    return request<RemarketingErasureResponse>('/remarketing/leads/erasure', {
      method: 'POST',
      body: { email },
      signal
    });
  }
};

export const adminApi = {
  getOverview(params: MetricsParams, signal?: AbortSignal): Promise<OverviewMetrics> {
    return request<OverviewMetrics>('/admin/metrics/overview', {
      query: { from: params.from, to: params.to, source: params.source },
      signal
    });
  },
  getFunnel(params: MetricsParams, signal?: AbortSignal): Promise<FunnelResponse> {
    return request<FunnelResponse>('/admin/metrics/funnel', {
      query: { from: params.from, to: params.to, source: params.source },
      signal
    });
  },
  listCheckouts(params: ListCheckoutsParams, signal?: AbortSignal): Promise<CheckoutListResponse> {
    return request<CheckoutListResponse>('/admin/checkouts', {
      query: {
        from: params.from,
        to: params.to,
        status: params.status,
        source: params.source,
        limit: params.limit,
        cursor: params.cursor ?? undefined
      },
      signal
    });
  },
  getCheckout(
    checkoutId: string,
    params: {
      limit?: number;
      source?: string;
      organizationId?: string;
      journeyId?: string;
    } = {},
    signal?: AbortSignal
  ): Promise<CheckoutDetails> {
    return request<CheckoutDetails>(`/admin/checkouts/${encodeURIComponent(checkoutId)}`, {
      query: {
        limit: params.limit,
        source: params.source,
        organizationId: params.organizationId,
        journeyId: params.journeyId
      },
      signal
    });
  },
  listEvents(params: ListEventsParams, signal?: AbortSignal): Promise<EventListResponse> {
    return request<EventListResponse>('/admin/events', {
      query: {
        from: params.from,
        to: params.to,
        eventName: params.eventName,
        limit: params.limit,
        cursor: params.cursor ?? undefined
      },
      signal
    });
  },
  eraseIdentity(anonymousId: string, signal?: AbortSignal): Promise<IdentityEraseResponse> {
    return request<IdentityEraseResponse>(`/admin/identities/${encodeURIComponent(anonymousId)}`, {
      method: 'DELETE',
      signal
    });
  }
};
